import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../common/Icon/Icon';

/**
 * The search filter rail. Everything is VISIBLE - no collapsed groups,
 * no carets to hunt through. Zones, top to bottom, by the question
 * each answers:
 *
 *   Availability + Near me  - WHO has/wants the cards
 *   Price                   - HOW much
 *   Game sections           - WHICH cards (sets / rarity / finish,
 *                             one zone per game, rendered from the
 *                             /api/onesearch/games config)
 *
 * Single-choice zones are pill rows (one lit at a time); rarity and
 * finish are toggle pills (any combination). Sort is NOT here - it
 * lives above the results, where ordering belongs. Active filters
 * also show as removable chips above the grid (OneSearch renders
 * those), so state is always legible.
 *
 * Mobile (<=760px): the rail opens as a bottom sheet (scrim, internal
 * scroll, "Show results" to close) from the Filters button OneSearch
 * puts right under the search box. Filters apply live either way.
 *
 * State contract: filters/near live in the URL and the sheet's open
 * state in OneSearch; this component renders them and calls back.
 * Only the set-search box is local.
 */

// Fallback while /api/onesearch/games loads
const DEFAULT_GAMES = [{
    id: 'mtg',
    label: 'Magic: The Gathering',
    hasSets: true,
    rarities: ['common', 'uncommon', 'rare', 'mythic'],
    finishes: ['nonfoil', 'foil', 'etched'],
}];

const AVAILS = [
    ['', 'All cards'],
    ['have', 'People have it'],
    ['want', 'People want it'],
];

let gamesCache = null; // static config - survives rail remounts

function FilterRail({
    open: mobileOpen, onClose, filters, near, nearAvailable, location, allSets, ensureSets,
    onPatch, onToggleList, onReset,
}) {
    const navigate = useNavigate();
    const [games, setGames] = useState(gamesCache || DEFAULT_GAMES);
    const [setQuery, setSetQuery] = useState('');

    useEffect(() => {
        if (gamesCache) return;
        fetch('/api/onesearch/games')
            .then(r => (r.ok ? r.json() : null))
            .then(d => {
                if (Array.isArray(d) && d.length) { gamesCache = d; setGames(d); }
            })
            .catch(() => { });
    }, []);

    // Mobile sheet open = the page behind must not scroll
    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    const setName = (code) => allSets.find(s => s.setCode === code)?.setName || code;

    const pill = (key, label, on, onClick, { disabled = false, cap = false } = {}) => (
        <button
            key={key}
            className={`os-pill${on ? ' on' : ''}${cap ? ' cap' : ''}`}
            onClick={onClick}
            disabled={disabled}
        >
            {label}
        </button>
    );

    const activeCount = (near ? 1 : 0) + (filters.avail ? 1 : 0)
        + filters.rarities.length + filters.finishes.length + filters.sets.length
        + (filters.priceMin !== '' ? 1 : 0) + (filters.priceMax !== '' ? 1 : 0);

    const setMatches = setQuery.trim().length >= 2
        ? allSets.filter(x =>
            (x.setName || '').toLowerCase().includes(setQuery.trim().toLowerCase()) ||
            (x.setCode || '').toLowerCase().includes(setQuery.trim().toLowerCase())
        ).slice(0, 8)
        : [];

    return (
        <>
            {mobileOpen && <div className="os-rail-scrim" onClick={onClose} />}

            <aside className={`os-rail${mobileOpen ? ' open' : ''}`}>
                <div className="os-sheet-head">
                    <span className="os-rail-title">
                        Filters{activeCount > 0 ? ` (${activeCount})` : ''}
                    </span>
                    <button
                        className="os-sheet-close"
                        onClick={onClose}
                        aria-label="Close filters"
                    >
                        ✕
                    </button>
                </div>
                <div className="os-rail-panel">
                    <div className="os-railhead">
                        <span className="os-rail-title">Filters</span>
                        {activeCount > 0 && (
                            <button className="os-rail-clearbtn" onClick={onReset}>↺ Reset</button>
                        )}
                    </div>

                    {/* WHO has/wants the cards */}
                    <div className="os-zone">
                        <div className="os-rail-title">Availability</div>
                        <div className="os-pillrow">
                            {AVAILS.map(([v, label]) =>
                                pill(v || 'any', label, filters.avail === v, () => onPatch({ avail: v || null })))}
                        </div>
                    </div>

                    <div className="os-zone">
                        <div className="os-rail-title">Near me</div>
                        {nearAvailable === false ? (
                            <button
                                className="os-setloc-btn"
                                onClick={() => navigate('/account?tab=preferences')}
                            >
                                <Icon name="pin" /> Set your location to unlock local search
                            </button>
                        ) : (
                            <>
                                <div className="os-range-row">
                                    <input
                                        type="range"
                                        className="os-range"
                                        style={{ '--fill': `${(near / 250) * 100}%` }}
                                        min="0" max="250" step="5"
                                        value={near}
                                        onChange={e => onPatch({ near: Number(e.target.value) || null })}
                                        aria-label="Near me radius"
                                    />
                                    <span className="os-range-val">{near ? `${near} mi` : 'Anywhere'}</span>
                                </div>
                                {location && <p className="os-rail-hint"><Icon name="pin" /> {location}</p>}
                            </>
                        )}
                    </div>

                    {/* HOW much */}
                    <div className="os-zone">
                        <div className="os-rail-title">Price</div>
                        <div className="os-rail-price">
                            <input
                                type="number" min="0" placeholder="Min"
                                value={filters.priceMin}
                                onChange={e => onPatch({ pmin: e.target.value })}
                            />
                            <span>–</span>
                            <input
                                type="number" min="0" placeholder="Max"
                                value={filters.priceMax}
                                onChange={e => onPatch({ pmax: e.target.value })}
                            />
                        </div>
                    </div>

                    {/* WHICH cards - one zone per game */}
                    {games.map(game => (
                        <div className="os-zone os-zone-game" key={game.id}>
                            <div className="os-rail-title">{game.label}</div>

                            {game.hasSets && (
                                <>
                                    <div className="os-rail-sub">Sets</div>
                                    {filters.sets.length > 0 && (
                                        <div className="os-rail-chips">
                                            {filters.sets.map(code => (
                                                <button
                                                    key={code}
                                                    className="os-rail-chip"
                                                    title="Remove"
                                                    onClick={() => onToggleList('sets', code)}
                                                >
                                                    {setName(code)} ×
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    <input
                                        className="os-rail-input"
                                        placeholder="Search sets…"
                                        value={setQuery}
                                        onFocus={ensureSets}
                                        onChange={e => setSetQuery(e.target.value)}
                                    />
                                    {setMatches.length > 0 && (
                                        <div className="os-rail-setlist">
                                            {setMatches.map(x => (
                                                <button
                                                    key={x.setCode}
                                                    className="os-rail-setrow"
                                                    onClick={() => {
                                                        if (!filters.sets.includes(x.setCode)) {
                                                            onToggleList('sets', x.setCode);
                                                        }
                                                        setSetQuery('');
                                                    }}
                                                >
                                                    {x.setName || x.setCode}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}

                            <div className="os-rail-sub">Rarity</div>
                            <div className="os-pillrow">
                                {game.rarities.map(r =>
                                    pill(r, r, filters.rarities.includes(r),
                                        () => onToggleList('rarities', r), { cap: true }))}
                            </div>

                            <div className="os-rail-sub">Finish</div>
                            <div className="os-pillrow">
                                {game.finishes.map(fin =>
                                    pill(fin, fin, filters.finishes.includes(fin),
                                        () => onToggleList('finishes', fin), { cap: true }))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="os-rail-panel">
                    <div className="os-rail-toolhead">Tools</div>
                    <button className="os-rail-tool" onClick={() => navigate('/traders')}>
                        <Icon name="users" /> Find traders
                    </button>
                    <button className="os-rail-tool" onClick={() => navigate('/middlemen')}>
                        <Icon name="shield" /> Middleman map
                    </button>
                </div>

                {/* Mobile sheet only: apply-and-close */}
                <button className="os-sheet-done" onClick={onClose}>
                    Show results
                </button>
            </aside>
        </>
    );
}

export default FilterRail;
